/**
 * Chantier : -
 * Regle metier : REF-R10 (SIRET etablissement modifiable seulement si pas d'objets actifs), REF-R21 (alias), PART-R06 (singleton SERME), USR-R04 (1 admin_client par etablissement), INT-PL-12..18 (liaison customer Pennylane)
 * Decision : D-ETAB-01 (SIRET garde applicative), D-ALIAS-01 (alias), D-AUTH-03 (invite admin_client), D-V1D-01 (EF point unique pennylane-customer), DV-2026-0061 (lookup PL existant)
 * Service applicatif Etablissement : CRUD + onboarding atomique (RPC + invite EF) + liaison
 * customer Pennylane (search/create/link/unlink, gestion sync_failures), garde SIRET (REF-R10).
 * L'invariant 1 admin_client est preserve par rollback en cas d'echec invite.
 */
import { supabase } from '@/lib/supabase';
import { searchCustomers, createCustomer, linkCustomer, unlinkCustomer } from '@/services/integrations/pennylane';
import { AppError, extractEdgeFunctionBody, extractEdgeFunctionError } from '@/lib/errors';
import type { Enums, TablesUpdate } from '@/types/supabase';
import { createInvitedUser, sendPasswordSetupLink, type InviteResult } from './users';

// Types BDD (snake_case)
export interface EtablissementRow {
  id: string;
  created_at: string;
  updated_at: string;
  siret: string;
  raison_sociale: string;
  alias: string | null;
  rue: string;
  adresse_complete: string | null;
  code_postal: string;
  ville: string;
  email_contact: string;
  emails_facturation: string[];
  telephone: string | null;
  is_active: boolean;
  is_serme: boolean;
  notif_nouvelles_soumissions: boolean;
  notif_nouvelles_demandes: boolean;
  notif_anomalies_td: boolean;
  notif_nouveaux_documents_chantier: boolean;
  pennylane_customer_id: string | null;
  centres_autorises_ids: string[] | null;
  transporteurs_autorises_ids: string[] | null;
}

// Types Front (camelCase)
export interface Etablissement {
  id: string;
  createdAt: string;
  updatedAt: string;
  siret: string;
  raisonSociale: string;
  alias?: string;
  rue: string;
  adresseComplete: string | null;
  codePostal: string;
  ville: string;
  emailContact: string;
  // D-FA-EMAILS-FACTURATION : destinataires de la facture finalisee (PDF en PJ) ; au moins une adresse pour finaliser.
  emailsFacturation: string[];
  telephone: string | null;
  isActive: boolean;
  isSerme: boolean;
  notifNouvellesSoumissions: boolean;
  notifNouvellesDemandes: boolean;
  notifAnomaliesTd: boolean;
  notifNouveauxDocumentsChantier: boolean;
  pennylaneCustomerId: string | null;
  centresAutorisesIds: string[];
  transporteursAutorisesIds: string[];
}

// REF-R21 / D-ALIAS-01 : alias modifiable par admin SERME uniquement (RLS ne
// l'enforce pas, garde applicative cote service).
export interface UpdateEtablissementInput {
  siret?: string;
  raisonSociale?: string;
  alias?: string | null;
  rue?: string;
  adresseComplete?: string;
  codePostal?: string;
  ville?: string;
  emailContact?: string;
  // Liste vide : plus d'adresse de facturation.
  emailsFacturation?: string[];
  telephone?: string | null;
  isActive?: boolean;
  notifNouvellesSoumissions?: boolean;
  notifNouvellesDemandes?: boolean;
  notifAnomaliesTd?: boolean;
  notifNouveauxDocumentsChantier?: boolean;
  centresAutorisesIds?: string[];
  transporteursAutorisesIds?: string[];
}

function mapRow(row: EtablissementRow): Etablissement {
  return {
    id: row.id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    siret: row.siret,
    raisonSociale: row.raison_sociale,
    alias: row.alias ?? undefined,
    rue: row.rue,
    adresseComplete: row.adresse_complete,
    codePostal: row.code_postal,
    ville: row.ville,
    emailContact: row.email_contact,
    emailsFacturation: row.emails_facturation,
    telephone: row.telephone,
    isActive: row.is_active,
    isSerme: row.is_serme,
    notifNouvellesSoumissions: row.notif_nouvelles_soumissions,
    notifNouvellesDemandes: row.notif_nouvelles_demandes,
    notifAnomaliesTd: row.notif_anomalies_td,
    notifNouveauxDocumentsChantier: row.notif_nouveaux_documents_chantier,
    pennylaneCustomerId: row.pennylane_customer_id,
    centresAutorisesIds: row.centres_autorises_ids ?? [],
    transporteursAutorisesIds: row.transporteurs_autorises_ids ?? [],
  };
}

export async function fetchEtablissements(): Promise<Etablissement[]> {
  const { data, error } = await supabase
    .from('etablissements')
    .select('*')
    .order('raison_sociale');

  if (error) throw error;
  return (data as EtablissementRow[]).map(mapRow);
}

export async function fetchEtablissement(id: string): Promise<Etablissement> {
  const { data, error } = await supabase
    .from('etablissements')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return mapRow(data as EtablissementRow);
}

// PART-R06 : singleton SERME (etablissements.is_serme = true) garanti unique en BDD via index
// unique partiel idx_etablissements_is_serme. Lecture exposee a tout authentifie via la RLS
// de etablissements (tout authentifie peut lire is_serme = true).
export async function fetchSermeEtablissement(): Promise<Etablissement> {
  const { data, error } = await supabase
    .from('etablissements')
    .select('*')
    .eq('is_serme', true)
    .single();

  if (error) throw error;
  return mapRow(data as EtablissementRow);
}

// Onboarding atomique : cree etablissement + admin_client en une transaction
export interface OnboardInput {
  siret: string;
  raisonSociale: string;
  alias?: string;
  rue: string;
  adresseComplete?: string;
  codePostal: string;
  ville: string;
  emailContact: string;
  telephoneEtab: string;
  adminNom: string;
  adminPrenom: string;
  adminEmail: string;
  // Obligatoire (D-ET-AUTO) : alimente users.telephone et le contact ET provisionne.
  adminTelephone: string;
  // D-ET-AUTO : provisionne l'entreprise de travaux (copie de l'etablissement) + son contact (= admin).
  creerEntrepriseTravaux: boolean;
}

export interface OnboardResult {
  etablissementId: string;
  userId: string | null;
  email: string | null;
  // Message utilisateur si le lien d'invitation n'a pas pu partir (compte cree, renvoi
  // possible depuis la fiche etablissement). Absent si l'email est parti.
  emailSendFailure?: string;
}

/**
 * Chantier : -
 * Regle metier : USR-R01 (1 admin_client actif par etablissement, INVARIANT), USR-R14, REF-R11
 * Decision : D-AUTH-05 (compte sans mot de passe + lien recovery), D-ET-AUTO (ET provisionnee)
 * Onboarding atomique etablissement + admin_client : RPC cree etablissement (+ tarifs auto,
 * + entreprise de travaux et son contact si demande), puis EF invite-user cree le compte admin
 * en_attente, puis envoi du lien recovery.
 * Rollback de l'etablissement (et de l'ET provisionnee) si la CREATION du compte echoue
 * (invariant 1 admin_client).
 * Si seul l'ENVOI du lien echoue (panne email, limite d'envoi), l'etablissement et son
 * admin en_attente sont conserves : le rollback laisserait un compte Auth orphelin qui
 * bloquerait toute nouvelle tentative, et le lien est renvoyable depuis la fiche.
 */
// Invariant PRD §2.8 : un etablissement a TOUJOURS exactement 1 admin_client (jamais 0).
export async function onboardEtablissement(input: OnboardInput): Promise<OnboardResult> {
  const { data, error } = await supabase.rpc('onboard_etablissement', {
    p_siret: input.siret,
    p_raison_sociale: input.raisonSociale,
    p_alias: input.alias || undefined,
    p_rue: input.rue,
    p_adresse_complete: input.adresseComplete || undefined,
    p_code_postal: input.codePostal,
    p_ville: input.ville,
    p_email_contact: input.emailContact,
    p_telephone_etab: input.telephoneEtab,
    p_admin_nom: input.adminNom,
    p_admin_prenom: input.adminPrenom,
    p_admin_email: input.adminEmail,
    p_admin_telephone: input.adminTelephone,
    p_creer_entreprise_travaux: input.creerEntrepriseTravaux,
  });

  if (error) throw error;
  const result = data as Record<string, unknown>;
  const etablissementId = result.etablissement_id as string;
  const adminEmail = result.admin_email as string | null;

  if (!adminEmail) {
    // Cas non prevu par le PRD §2.6 (admin_email obligatoire a l'onboarding).
    // Le frontend force le champ, mais defense en profondeur : on echoue explicitement
    // et on rollback pour preserver l'invariant PRD §2.8.
    await supabase.rpc('rollback_onboard_etablissement', { p_etablissement_id: etablissementId });
    throw new Error('Onboarding sans admin_client interdit par PRD §2.6');
  }

  let inviteResult: InviteResult;
  try {
    inviteResult = await createInvitedUser({
      email: adminEmail,
      prenom: (result.admin_prenom as string | null) ?? '',
      nom: (result.admin_nom as string | null) ?? '',
      telephone: (result.admin_telephone as string | null) ?? undefined,
      role: 'admin_client',
      etablissementId,
    });
  } catch (err) {
    // Rollback : RPC qui supprime atomiquement l'etablissement + tarifs_traitement
    // auto-crees + row public.users en_attente eventuelle. Preserve l'invariant
    // PRD §2.8 (jamais 0 admin_client par etablissement).
    const { error: rollbackError } = await supabase.rpc('rollback_onboard_etablissement', {
      p_etablissement_id: etablissementId,
    });
    if (rollbackError) {
      console.error('[onboardEtablissement] Rollback echoue', rollbackError);
    }
    throw err;
  }

  // Envoi du lien : un echec ne remet pas en cause la creation (cf. header).
  try {
    await sendPasswordSetupLink(adminEmail);
  } catch (err) {
    if (!(err instanceof AppError)) throw err;
    return { etablissementId, userId: inviteResult.userId, email: adminEmail, emailSendFailure: err.userMessage };
  }

  return { etablissementId, userId: inviteResult.userId, email: adminEmail };
}

// =============================================================================
// Liaison customer Pennylane (DV-2026-0061 LOT-03 ; INT-PL-12 a INT-PL-18)
// Edge Function "pennylane-customer" actions : search, create, link_existing, unlink.
// =============================================================================

export interface PennylaneCustomerSearchResult {
  plCustomerId: string;
  name: string | null;
  siret: string | null;
  alreadyLinkedTo: { etablissementId: string; raisonSociale: string } | null;
}

// INT-PL-13 (variante A) : recherche deterministe par SIRET ou ID PL natif.
export type PennylaneSearchMode = 'siret' | 'pl_id';

export async function searchPennylaneCustomers(
  query: string,
  mode: PennylaneSearchMode,
): Promise<PennylaneCustomerSearchResult[]> {
  const { data, error } = await searchCustomers({ query, mode });
  if (error) throw new Error(await extractEdgeFunctionError(error));
  if (!data?.success) throw new Error((data as { error?: string })?.error || 'Erreur recherche Pennylane');
  return (data as { items: PennylaneCustomerSearchResult[] }).items;
}

export interface PennylaneCustomerSiretMatch {
  plCustomerId: string;
  name: string | null;
  siret: string;
}

export type CreateOrPromptResult =
  | { kind: 'created'; plCustomerId: string }
  | { kind: 'idempotent'; plCustomerId: string }
  | { kind: 'confirm_link'; existingCustomers: PennylaneCustomerSiretMatch[] }
  | { kind: 'sync_failure'; plCustomerId: string; error: string }
  | { kind: 'error'; error: string };

/**
 * Chantier : -
 * Regle metier : INT-PL-12 (liaison customer PL), INT-PL-15 (lookup SIRET anti-doublon), INT-PL-17 (creation customer PL)
 * Decision : D-V1D-01 (EF point unique : POST PL OK + UPDATE BDD KO -> sync_failure retry_bdd_update)
 * Creation customer PL avec lookup SIRET prealable cote EF. Renvoie 4 cas typed :
 * created (succes), confirm_link (1+ candidats SIRET, modale anti-doublon), sync_failure
 * (PL ok mais BDD KO, retry possible), error (echec).
 */
// INT-PL-15 : creation customer PL avec lookup SIRET prealable cote EF.
export async function createOrPromptLinkPennylaneCustomer(
  etablissementId: string,
  etab: { raisonSociale: string; siret: string; emailContact: string; rue: string; codePostal: string; ville: string },
): Promise<CreateOrPromptResult> {
  const { data, error } = await createCustomer({
    etablissementId,
    name: etab.raisonSociale,
    siret: etab.siret,
    email: etab.emailContact,
    address: etab.rue,
    postalCode: etab.codePostal,
    city: etab.ville,
  });

  if (error) {
    // D-V1D-01 : POST PL OK + UPDATE BDD KO -> 500 retry_bdd_update
    const body = await extractEdgeFunctionBody(error);
    if (body?.action === 'retry_bdd_update' && typeof body.plCustomerId === 'string') {
      return { kind: 'sync_failure', plCustomerId: body.plCustomerId, error: typeof body.error === 'string' ? body.error : 'BDD non synchronisee' };
    }
    return { kind: 'error', error: await extractEdgeFunctionError(error) };
  }
  if (!data?.success) {
    return { kind: 'error', error: (data as { error?: string })?.error || 'Erreur creation Pennylane' };
  }

  // INT-PL-15 : SIRET match (1 ou plusieurs candidats) -> bascule modale anti-doublon
  const typed = data as { action_required?: string; existingCustomers?: PennylaneCustomerSiretMatch[]; plCustomerId?: string; idempotent?: boolean };
  if (typed.action_required === 'confirm_link' && Array.isArray(typed.existingCustomers) && typed.existingCustomers.length > 0) {
    return { kind: 'confirm_link', existingCustomers: typed.existingCustomers };
  }
  // INT-PL-11 : pre-check 1 BDD ou pre-check 2 PL ont detecte un lien deja en place.
  // Aucune creation cote PL n'a eu lieu. Toast distinct pour ne pas tromper l'utilisateur.
  if (typed.idempotent === true) {
    return { kind: 'idempotent', plCustomerId: String(typed.plCustomerId) };
  }
  return { kind: 'created', plCustomerId: String(typed.plCustomerId) };
}

export type LinkExistingResult =
  | { kind: 'linked'; plCustomerId: string }
  | { kind: 'already_linked'; currentPlCustomerId: string }
  | { kind: 'customer_already_used' }
  | { kind: 'error'; error: string };

/**
 * Chantier : -
 * Regle metier : INT-PL-13 (typeahead recherche customer), INT-PL-15 (anti-doublon "lier")
 * Decision : -
 * Lie un customer PL existant a un etablissement (depuis selection typeahead ou modale
 * anti-doublon). Renvoie 4 cas : linked, already_linked, customer_already_used, error.
 */
// INT-PL-13 (typeahead) + INT-PL-15 (anti-doublon "lier") : lie un customer PL existant.
export async function linkPennylaneCustomer(
  etablissementId: string,
  plCustomerId: string,
): Promise<LinkExistingResult> {
  const { data, error } = await linkCustomer({ etablissementId, plCustomerId });

  if (error) {
    const body = await extractEdgeFunctionBody(error);
    if (body?.error === 'already_linked' && typeof body.currentPlCustomerId === 'string') {
      return { kind: 'already_linked', currentPlCustomerId: body.currentPlCustomerId };
    }
    if (body?.error === 'customer_already_used') {
      return { kind: 'customer_already_used' };
    }
    return { kind: 'error', error: await extractEdgeFunctionError(error) };
  }
  if (!data?.success) {
    return { kind: 'error', error: (data as { error?: string })?.error || 'Erreur liaison Pennylane' };
  }
  return { kind: 'linked', plCustomerId: String((data as { plCustomerId: string }).plCustomerId) };
}

// INT-PL-16 : vidage du lien PL (la garde objets actifs vit cote UI/service).
export async function unlinkPennylaneCustomer(etablissementId: string): Promise<void> {
  const { data, error } = await unlinkCustomer({ etablissementId });
  if (error) throw new Error(await extractEdgeFunctionError(error));
  if (!data?.success) throw new Error((data as { error?: string })?.error || 'Erreur deliaison Pennylane');
}

/**
 * Chantier : -
 * Regle metier : INT-PL-16 (deliaison customer PL conditionnee par absence d'objets PL actifs)
 * Decision : -
 * Compte les devis et factures non-terminaux d'un etablissement pour conditionner la
 * modifiabilite du PL_ID. Filtrage NOT IN (statuts terminaux) -> defense en profondeur :
 * un statut PL inconnu compte comme actif.
 */
// INT-PL-16 : compte les objets PL actifs rattaches a un etablissement
// (devis non terminal ou facture non terminale) pour conditionner la modifiabilite du PL_ID.
// On filtre par statuts TERMINAUX (NOT IN) pour etre robuste a l'ajout de nouveaux statuts
// PL : un statut inconnu compte comme "actif" par defaut, ce qui protege la garde.
//  - devis terminaux : denied, invoiced
//  - factures terminales : cancelled, credit_note
const DEVIS_STATUTS_TERMINAUX = ['denied', 'invoiced'];
const FACTURES_STATUTS_TERMINAUX = ['cancelled', 'credit_note'];

// PostgREST : .not('statut','in','(a,b)') exige des valeurs CSV sans guillemets.
function notInCsv(values: string[]): string {
  return `(${values.join(',')})`;
}

export async function countActivePlObjects(etablissementId: string): Promise<{
  devis: number;
  factures: number;
  total: number;
}> {
  // Devis et factures n'ont pas d'etablissement_id direct : ils sont rattaches XOR a un
  // chantier ou un transport. On recupere d'abord les ids des chantiers/transports de
  // l'etablissement, puis on filtre les devis/factures via chantier_id IN (...) OR transport_id IN (...).
  const [chantierRes, transportRes] = await Promise.all([
    supabase.from('chantiers').select('id').eq('etablissement_id', etablissementId),
    supabase.from('transports').select('id').eq('etablissement_id', etablissementId),
  ]);

  // Ne pas masquer les erreurs Supabase : sinon hasActivePlObjects passerait a `false`
  // par defaut et les boutons "Modifier"/"Delier" deviendraient activables alors que la garde
  // n'a pas pu etre verifiee.
  if (chantierRes.error) throw chantierRes.error;
  if (transportRes.error) throw transportRes.error;

  const chantierIds = (chantierRes.data ?? []).map((r: { id: string }) => r.id);
  const transportIds = (transportRes.data ?? []).map((r: { id: string }) => r.id);

  if (chantierIds.length === 0 && transportIds.length === 0) {
    return { devis: 0, factures: 0, total: 0 };
  }

  // Filtre OR entre 2 colonnes FK + filtre NOT IN sur statut. PostgREST supporte .or().
  const orParts: string[] = [];
  if (chantierIds.length > 0) orParts.push(`chantier_id.in.(${chantierIds.join(',')})`);
  if (transportIds.length > 0) orParts.push(`transport_id.in.(${transportIds.join(',')})`);
  const orFilter = orParts.join(',');

  const [devisRes, facturesRes] = await Promise.all([
    supabase
      .from('devis')
      .select('id', { count: 'exact', head: true })
      .or(orFilter)
      .not('statut', 'in', notInCsv(DEVIS_STATUTS_TERMINAUX)),
    supabase
      .from('factures')
      .select('id', { count: 'exact', head: true })
      .or(orFilter)
      .not('statut', 'in', notInCsv(FACTURES_STATUTS_TERMINAUX)),
  ]);

  if (devisRes.error) throw devisRes.error;
  if (facturesRes.error) throw facturesRes.error;

  const devis = devisRes.count ?? 0;
  const factures = facturesRes.count ?? 0;
  return { devis, factures, total: devis + factures };
}

// REF-R10 / D-ETAB-01 : statuts operationnels actifs qui bloquent la modification du SIRET.
// brouillon et statuts terminaux (clos/annule pour chantiers, traite/annule pour transports)
// sont exclus du compte.
const CHANTIER_STATUTS_ACTIFS: Enums<'statut_chantier'>[] = ['en_cours_chiffrage', 'chiffre', 'en_attente_traitement', 'en_cours'];
const TRANSPORT_STATUTS_ACTIFS: Enums<'statut_transport'>[] = ['en_attente_chiffrage', 'chiffre', 'a_qualifier', 'qualifie', 'planifie'];

/**
 * REF-R10 : compte les chantiers et transports actifs rattaches a un etablissement.
 *
 * Sert de garde pour la modification du SIRET d'un etablissement (D-ETAB-01).
 * Si total > 0, la modification est rejetee avec message detaille.
 */
export async function countActiveChantiersTransports(etablissementId: string): Promise<{
  chantiers: number;
  transports: number;
  total: number;
}> {
  const [chantierRes, transportRes] = await Promise.all([
    supabase
      .from('chantiers')
      .select('id', { count: 'exact', head: true })
      .eq('etablissement_id', etablissementId)
      .in('statut', CHANTIER_STATUTS_ACTIFS),
    supabase
      .from('transports')
      .select('id', { count: 'exact', head: true })
      .eq('etablissement_id', etablissementId)
      .in('statut', TRANSPORT_STATUTS_ACTIFS),
  ]);

  if (chantierRes.error) throw chantierRes.error;
  if (transportRes.error) throw transportRes.error;

  const chantiers = chantierRes.count ?? 0;
  const transports = transportRes.count ?? 0;
  return { chantiers, transports, total: chantiers + transports };
}

/**
 * Erreur metier specialisee : modification SIRET refusee car des chantiers / transports
 * actifs existent (REF-R10). L'UI consomme `chantiers` / `transports` pour afficher
 * un toast detaille.
 */
export class SiretChangeBlockedError extends Error {
  readonly chantiers: number;
  readonly transports: number;

  constructor(chantiers: number, transports: number) {
    super(
      `Modification du SIRET impossible : ${chantiers} chantier(s) et ${transports} transport(s) actifs sont rattaches a cet etablissement. Annulez ou clôturez ces opérations avant de modifier le SIRET.`,
    );
    this.name = 'SiretChangeBlockedError';
    this.chantiers = chantiers;
    this.transports = transports;
  }
}

/**
 * Chantier : -
 * Regle metier : REF-R10 (modification SIRET refusee si chantiers/transports actifs)
 * Decision : D-ETAB-01 (garde applicative SIRET, pas de RPC PG)
 * Update etablissement : verifie si SIRET change qu'aucun chantier/transport actif n'est
 * rattache (sinon SiretChangeBlockedError). Race condition negligeable (1 admin SERME a la fois).
 */
export async function updateEtablissement(
  id: string,
  input: UpdateEtablissementInput
): Promise<Etablissement> {
  // REF-R10 / D-ETAB-01 : si le SIRET change, verifier qu'aucun chantier ou transport
  // actif n'est rattache. La verification se fait ici (et non dans une RPC PG) parce que
  // le risque de race condition est negligeable (1 admin SERME a la fois en pratique).
  if (input.siret !== undefined) {
    const { data: current, error: fetchErr } = await supabase
      .from('etablissements')
      .select('siret')
      .eq('id', id)
      .single();
    if (fetchErr) throw fetchErr;
    const currentSiret = (current as { siret: string }).siret;
    if (input.siret !== currentSiret) {
      const counts = await countActiveChantiersTransports(id);
      if (counts.total > 0) {
        throw new SiretChangeBlockedError(counts.chantiers, counts.transports);
      }
    }
  }

  const updates: TablesUpdate<'etablissements'> = {};
  if (input.siret !== undefined) updates.siret = input.siret;
  if (input.raisonSociale !== undefined) updates.raison_sociale = input.raisonSociale;
  if (input.alias !== undefined) updates.alias = input.alias === null ? null : (input.alias.trim() || null);
  if (input.rue !== undefined) updates.rue = input.rue;
  if (input.adresseComplete !== undefined) updates.adresse_complete = input.adresseComplete;
  if (input.codePostal !== undefined) updates.code_postal = input.codePostal;
  if (input.ville !== undefined) updates.ville = input.ville;
  if (input.emailContact !== undefined) updates.email_contact = input.emailContact;
  if (input.emailsFacturation !== undefined) updates.emails_facturation = input.emailsFacturation;
  if (input.telephone !== undefined) updates.telephone = input.telephone;
  if (input.isActive !== undefined) updates.is_active = input.isActive;
  if (input.notifNouvellesSoumissions !== undefined) updates.notif_nouvelles_soumissions = input.notifNouvellesSoumissions;
  if (input.notifNouvellesDemandes !== undefined) updates.notif_nouvelles_demandes = input.notifNouvellesDemandes;
  if (input.notifAnomaliesTd !== undefined) updates.notif_anomalies_td = input.notifAnomaliesTd;
  if (input.notifNouveauxDocumentsChantier !== undefined) updates.notif_nouveaux_documents_chantier = input.notifNouveauxDocumentsChantier;
  if (input.centresAutorisesIds !== undefined) updates.centres_autorises_ids = input.centresAutorisesIds;
  if (input.transporteursAutorisesIds !== undefined) updates.transporteurs_autorises_ids = input.transporteursAutorisesIds;

  const { data, error } = await supabase
    .from('etablissements')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return mapRow(data as EtablissementRow);
}
